/**
 * User Products Component
 * Displays owned products with download functionality
 * Follows the unified UX pattern with stats, filtering, and modern card design
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Download,
  FolderOpen,
  Calendar,
  RefreshCw,
  Filter,
  ArrowUpDown,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import { OwnedProduct } from '../../services/indexedDB';
import { downloadsApi } from '../../services/api';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import { QuickActionsGrid, QuickAction } from '../common/QuickActionsGrid';

interface UserProductsProps {
  products: OwnedProduct[];
  syncing: boolean;
  onDownloadComplete?: () => void;
}

type SortOption = 'newest' | 'oldest' | 'title-asc' | 'title-desc';
type FilterOption = 'all' | string;

export default function UserProducts({ products, syncing, onDownloadComplete }: UserProductsProps): JSX.Element {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(products.map(p => p.category));
    return Array.from(cats).sort();
  }, [products]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalDownloads = products.reduce((sum, p) => sum + p.downloadCount, 0);
    const productsWithLimits = products.filter(p => p.downloadLimit !== -1);
    const totalLimitedDownloads = productsWithLimits.reduce((sum, p) => sum + p.downloadLimit, 0);

    return {
      total: products.length,
      categories: categories.length,
      downloads: totalDownloads,
      availableDownloads: totalLimitedDownloads > 0
        ? totalLimitedDownloads - productsWithLimits.reduce((sum, p) => sum + p.downloadCount, 0)
        : -1, // -1 means unlimited
    };
  }, [products, categories]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Products',
      value: stats.total,
      icon: Package,
      variant: 'primary',
    },
    {
      id: 'categories',
      label: 'Categories',
      value: stats.categories,
      icon: FolderOpen,
      variant: 'success',
    },
    {
      id: 'downloads',
      label: 'Downloads Used',
      value: stats.downloads,
      icon: Download,
      variant: 'default',
    },
  ], [stats]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Apply filter
    if (filterBy !== 'all') {
      result = result.filter(p => p.category === filterBy);
    }

    // Apply sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime();
        case 'oldest':
          return new Date(a.purchasedAt).getTime() - new Date(b.purchasedAt).getTime();
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    return result;
  }, [products, filterBy, sortBy]);

  // Handle download
  async function handleDownload(product: OwnedProduct) {
    if (product.downloadLimit !== -1 && product.downloadCount >= product.downloadLimit) {
      return;
    }

    setDownloading(product.id);
    setDownloadSuccess(null);

    try {
      await downloadsApi.downloadProduct(product.orderId, product.id);
      setDownloadSuccess(product.id);

      // Clear success indicator after 3 seconds
      setTimeout(() => setDownloadSuccess(null), 3000);

      if (onDownloadComplete) {
        onDownloadComplete();
      }
    } catch (error: any) {
      console.error('Download failed:', error);
      alert(error.message || 'Failed to download product');
    } finally {
      setDownloading(null);
    }
  }

  // Format date
  const formatDate = (date: string): string => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  // Format category name
  const formatCategory = (category: string): string => {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get download status
  const getDownloadStatus = (product: OwnedProduct) => {
    if (product.downloadLimit === -1) {
      return { unlimited: true, remaining: -1, percentage: 0 };
    }
    const remaining = product.downloadLimit - product.downloadCount;
    const percentage = (product.downloadCount / product.downloadLimit) * 100;
    return { unlimited: false, remaining, percentage };
  };

  // Empty state
  if (products.length === 0) {
    const emptyActions: QuickAction[] = [
      {
        id: 'browse',
        title: 'Browse Products',
        description: 'Explore our collection of strategy guides',
        icon: 'package',
        linkUrl: '/products',
        buttonText: 'View Products',
        variant: 'primary',
      },
      {
        id: 'orders',
        title: 'View Orders',
        description: 'Check your order history and status',
        icon: 'shopping-bag',
        linkUrl: '/user?tab=orders',
        buttonText: 'View Orders',
        variant: 'default',
      },
    ];

    return (
      <div className="space-y-8">
        {/* Syncing Indicator */}
        {syncing && (
          <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <RefreshCw size={14} className="animate-spin mr-2" />
            Syncing your products...
          </div>
        )}

        {/* Empty State */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
              <Package size={48} className="text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            No Products Yet
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            You haven't purchased any products yet. Browse our collection to find the perfect strategy guide for you.
          </p>
          <QuickActionsGrid actions={emptyActions} columns={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Syncing Indicator */}
      {syncing && (
        <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <RefreshCw size={14} className="animate-spin mr-2" />
          Syncing your products...
        </div>
      )}

      {/* Stats Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Product Library
        </h2>
        <StatsWidget stats={statItems} columns={3} />
      </section>

      {/* Filter and Sort Controls */}
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Category:</span>
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{formatCategory(cat)}</option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowUpDown size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title-asc">Title (A-Z)</option>
              <option value="title-desc">Title (Z-A)</option>
            </select>
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredProducts.map((product) => {
          const downloadStatus = getDownloadStatus(product);
          const isDownloading = downloading === product.id;
          const isSuccess = downloadSuccess === product.id;
          const canDownload = downloadStatus.unlimited || downloadStatus.remaining > 0;

          return (
            <div
              key={product.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Product Header */}
              <div className="p-5">
                {/* Category Badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                    <FolderOpen size={12} className="mr-1" />
                    {formatCategory(product.category)}
                  </span>
                  {isSuccess && (
                    <span className="inline-flex items-center text-xs text-green-600 dark:text-green-400">
                      <CheckCircle size={14} className="mr-1" />
                      Downloaded!
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 line-clamp-2">
                  {product.title}
                </h3>

                {/* Meta Info */}
                <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} />
                    <span>Purchased {formatDate(product.purchasedAt)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText size={14} />
                    <span>Order #{product.orderId.slice(0, 8).toUpperCase()}</span>
                  </div>
                </div>
              </div>

              {/* Download Section */}
              <div className="px-5 pb-5">
                {/* Download Progress (for limited downloads) */}
                {!downloadStatus.unlimited && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-gray-500 dark:text-gray-400">
                        Downloads used
                      </span>
                      <span className={`font-medium ${
                        downloadStatus.remaining === 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {product.downloadCount} / {product.downloadLimit}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          downloadStatus.percentage >= 100
                            ? 'bg-red-500'
                            : downloadStatus.percentage >= 75
                            ? 'bg-amber-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(downloadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    {downloadStatus.remaining > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        {downloadStatus.remaining} download{downloadStatus.remaining !== 1 ? 's' : ''} remaining
                      </p>
                    )}
                  </div>
                )}

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(product)}
                  disabled={isDownloading || !canDownload}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all inline-flex items-center justify-center gap-2 ${
                    !canDownload
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : isDownloading
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                  }`}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Downloading...
                    </>
                  ) : !canDownload ? (
                    <>
                      <AlertCircle size={18} />
                      Limit Reached
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Download
                    </>
                  )}
                </button>

                {/* Unlimited badge */}
                {downloadStatus.unlimited && (
                  <p className="text-xs text-center text-green-600 dark:text-green-400 mt-2 flex items-center justify-center gap-1">
                    <CheckCircle size={12} />
                    Unlimited downloads
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {/* No Results After Filter */}
      {filteredProducts.length === 0 && products.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <Filter size={32} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            No products match your filter. Try selecting a different category.
          </p>
          <button
            onClick={() => setFilterBy('all')}
            className="mt-4 text-primary-600 dark:text-primary-400 font-medium hover:underline"
          >
            Show all products
          </button>
        </div>
      )}

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Having trouble with downloads?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Check our help center for download guides and troubleshooting
              </p>
            </div>
          </div>
          <Link to="/help" className="btn btn-primary whitespace-nowrap">
            Get Help
          </Link>
        </div>
      </section>
    </div>
  );
}
